/**
 * Screen 01 · Dashboard — the derived numbers behind the tiles.
 *
 * The dashboard invents nothing. Every figure on it is already stored
 * somewhere else: prep completions, the venue's spec overrides, the last
 * count's lines, the week's wastage and expenses, the menu's costing. What
 * this module does is *roll those up* — pick the week, band the variance,
 * count what is open — and it does it here rather than in the component for
 * rule 4's reason: the API and the SPA must agree to the cent, and a
 * dashboard that disagrees with the screen it links to is worse than no
 * dashboard at all.
 *
 * Nothing here fetches. Callers hand in what they already read.
 */
import { money, resolveCostPerMl, toCents } from "./calc.js";
import { expenseTotals } from "./expenses.js";
import { DEFAULT_GP_TARGET } from "./menu.js";
import { DEFAULT_TIMEZONE, serviceDateIn, weekBoundsOf, WEEK_STARTS_ON, } from "./time.js";
import { wastageSummary, } from "./wastage.js";
export const DASHBOARD_ROLES = [
    "bartender",
    "manager",
    "owner",
];
/** The prototype's `roleName` — what the left column prints. */
export const ROLE_NAME = {
    bartender: "Bartender",
    manager: "Manager",
    owner: "Owner",
};
export function dashboardRole(orgRole, venueRole) {
    if (orgRole === "owner" || orgRole === "admin")
        return "owner";
    if (venueRole === "manager")
        return "manager";
    return "bartender";
}
const RANK = {
    bartender: 0,
    manager: 1,
    owner: 2,
};
/** True when `role` sees everything `at` sees. Owner ⊇ manager ⊇ bartender. */
export function sees(role, at) {
    return RANK[role] >= RANK[at];
}
export function venueGapNotice({ venueCount, canManageOrg, what, }) {
    if (venueCount <= 0) {
        return {
            gap: "org-has-no-venues",
            title: "This organisation has no venues yet.",
            detail: canManageOrg
                ? `Add your first venue on screen 12 · Organisation and ${what} will have somewhere to live.`
                : `There will be ${what} once an owner or an admin adds the first venue.`,
            action: canManageOrg ? "Add a venue" : null,
        };
    }
    const venues = `${venueCount} venue${venueCount === 1 ? "" : "s"}`;
    return {
        gap: "not-assigned",
        title: "You are not assigned to a venue.",
        detail: canManageOrg
            ? `This organisation has ${venues}. Put yourself on one on screen 12 · Organisation to see ${what}.`
            : `This organisation has ${venues}. Ask a manager or an organisation admin to add you to one, and ${what} will open here.`,
        action: canManageOrg ? "Open organisation" : null,
    };
}
/**
 * The trading week containing a service date.
 *
 * `weekWindow("2026-09-19")` — a Friday — is Monday the 14th through Sunday
 * the 20th. Takes a service date, not an instant, so the caller has already
 * decided which night a 01:30 tick belongs to (see `serviceDate`). Named for
 * the window it returns; `weekBoundsOf` in `time.ts` is the same arithmetic.
 */
export function weekWindow(date, startsOn = WEEK_STARTS_ON) {
    const { start, end } = weekBoundsOf(date, startsOn);
    return { from: start, to: end };
}
/**
 * Is a stored timestamp inside the window?
 *
 * The window is a span of *service dates*, so the question is which service a
 * stored instant belongs to — and that is decided on the venue's wall clock,
 * not in UTC. Melbourne runs ten or eleven hours ahead: Sunday's last entry
 * at 11 pm is `13:00Z`, and comparing UTC to the date strings the way this
 * used to put it in next week's figures under this week's label. That was
 * issue #60. The `to` end runs to the close of Sunday's *service* — four in
 * the morning, the same rollover `serviceDate` applies — not to local
 * midnight, so a Sunday night's closing wastage counts in the week it was
 * poured in.
 *
 * The wastage and expenses routes bound their `from`/`to` queries with
 * `periodBounds` in the same zone, so filtering a full list in the browser
 * and asking the API for the same span still give the same rows.
 */
export function isWithinWindow(at, window, timeZone = DEFAULT_TIMEZONE) {
    const instant = Date.parse(at);
    if (!Number.isFinite(instant))
        return false;
    const date = serviceDateIn(new Date(instant), timeZone);
    return date >= window.from && date <= window.to;
}
/**
 * Month names, spelled out rather than taken from `toLocaleDateString`.
 *
 * `Intl` under en-AU abbreviates September as "Sept", and which ICU a runtime
 * carries decides whether it does. A label on a tile should not read
 * differently on Lambda and in the browser, so it is a table.
 */
const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
/** `2026-09-14` … `2026-09-20` → `14–20 Sep`; one night → `14 Sep`. */
export function windowLabel(window) {
    const from = new Date(`${window.from}T00:00:00Z`);
    const to = new Date(`${window.to}T00:00:00Z`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        return `${window.from} – ${window.to}`;
    }
    const month = (d) => MONTHS[d.getUTCMonth()] ?? "";
    const day = (d) => String(d.getUTCDate());
    // A single night is a date, not a span: a daily report read against
    // "14–14 Sep" reads like a fault in the report.
    if (window.from === window.to)
        return `${day(to)} ${month(to)}`;
    return month(from) === month(to)
        ? `${day(from)}–${day(to)} ${month(to)}`
        : `${day(from)} ${month(from)} – ${day(to)} ${month(to)}`;
}
/**
 * Tonight's prep, counted by tab.
 *
 * Empty tabs are kept. A tab with nothing in it is information — it says the
 * closing list has not been written, not that closing has been done — and
 * dropping it would make the dashboard silently shorter than screen 06.
 */
export function prepSummary(tabs) {
    const rolled = tabs.map((group) => {
        const total = group.tasks.length;
        const done = group.tasks.filter((t) => t.done).length;
        return { tab: group.tab, open: total - done, done, total };
    });
    return {
        tabs: rolled,
        open: rolled.reduce((n, t) => n + t.open, 0),
        done: rolled.reduce((n, t) => n + t.done, 0),
        total: rolled.reduce((n, t) => n + t.total, 0),
    };
}
export const DEFAULT_86_REASON = "86'd";
/**
 * What the venue has taken off tonight: specs the org lists as available
 * whose venue override says otherwise.
 *
 * Only `available` specs count. A retired drink that also carries
 * `available: false` at the venue is not news to the person opening the bar;
 * the drinks worth naming are the ones a guest could reasonably order.
 */
export function eightySixed(specs, overrides) {
    return specs
        .filter((spec) => spec.status === "available")
        .flatMap((spec) => {
        const override = overrides.get(spec.specId);
        if (override?.available !== false)
            return [];
        const reason = override.eightySixReason?.trim();
        return [
            {
                specId: spec.specId,
                name: spec.name,
                reason: reason === undefined || reason === "" ? DEFAULT_86_REASON : reason,
            },
        ];
    })
        .sort((a, b) => a.name.localeCompare(b.name));
}
/** How many flagged lines a tile shows before it stops being a tile. */
export const FLAGGED_LIMIT = 6;
/** Round to whole cents and back to dollars. Money is never left as float dust. */
function cents(dollars) {
    return toCents(dollars) / 100;
}
/**
 * Roll one count's lines into what a manager needs at a glance: how many are
 * outside tolerance, which ones, and what the gap is worth.
 *
 * Cost per millilitre is resolved the same way everything else resolves it —
 * the org's figure unless the venue overrides it — and handed in by the
 * caller, because this module does not know what an ingredient is.
 *
 * Only `watch` and `investigate` lines are listed. A count is a hundred rows
 * and ninety of them are fine; the tile's job is the ten that are not.
 */
export function varianceRollUp(lines, costPerMl, options = {}) {
    const counts = { ok: 0, watch: 0, investigate: 0 };
    const rows = lines.map((line) => {
        counts[line.band] += 1;
        return { ...line, costDollars: cents(line.deltaMl * costPerMl(line.ingredientId)) };
    });
    const flagged = rows
        .filter((r) => r.band !== "ok")
        .sort((a, b) => Math.abs(b.percent) - Math.abs(a.percent) ||
        a.ingredientId.localeCompare(b.ingredientId))
        .slice(0, options.limit ?? FLAGGED_LIMIT);
    return {
        lines: rows.length,
        counts,
        flagged,
        netCostDollars: cents(rows.reduce((total, r) => total + r.costDollars, 0)),
        shortfallDollars: cents(rows.reduce((total, r) => total + (r.costDollars < 0 ? -r.costDollars : 0), 0)),
    };
}
/**
 * Cost per millilitre for one ingredient at one venue.
 *
 * A convenience over `resolveCostPerMl` for the shape the dashboard has: the
 * org library keyed by id, and the venue's `STOCK#` rows which may carry an
 * override. An ingredient nobody has costed is zero, not a throw — a count
 * line for it still has a variance worth showing, it just has no dollars.
 */
export function costPerMlLookup(ingredients, stock) {
    const overrides = new Map(stock.map((s) => [s.ingredientId, s.costPerMl]));
    return (ingredientId) => resolveCostPerMl(ingredients.get(ingredientId)?.costPerMl ?? 0, overrides.get(ingredientId));
}
/** Rows inside the window. The one place the dashboard decides "this week". */
export function withinWindow(rows, window, timeZone = DEFAULT_TIMEZONE) {
    return rows.filter((row) => isWithinWindow(row.at, window, timeZone));
}
/**
 * This week's wastage, by reason.
 *
 * The totals come from `wastageSummary`, unchanged, so the dashboard's
 * figures are the same arithmetic the wastage route returns. What is added is
 * the window — the screen holds the whole list and picks the week out of it —
 * and a count per reason, because "4 spills · $41.20" reads better than
 * either half on its own.
 */
export function wastageForWeek(rows, window, timeZone = DEFAULT_TIMEZONE) {
    const within = withinWindow(rows, window, timeZone);
    const summary = wastageSummary(within);
    const counted = new Map();
    for (const row of within)
        counted.set(row.reason, (counted.get(row.reason) ?? 0) + 1);
    return {
        ...summary,
        window,
        reasons: summary.byReason.map((r) => ({ ...r, count: counted.get(r.reason) ?? 0 })),
    };
}
/**
 * This week's expenses. Same window rule, and `expenseTotals` for the sums,
 * so the tile and screen 11 cannot disagree about what a week cost.
 */
export function expensesForWeek(rows, window, timeZone = DEFAULT_TIMEZONE) {
    return { ...expenseTotals(withinWindow(rows, window, timeZone)), window };
}
/**
 * Orders waiting on an approval.
 *
 * `submitted` is precisely that state and nothing else is: screen 10 puts an
 * order within the raiser's limit straight to `queued`, and only one over it
 * stops at `submitted` for someone senior. So this is a status filter rather
 * than a guess at what "open" means — a draft is somebody still typing, and a
 * queued or sent order is gone.
 */
export function ordersAwaitingApproval(orders) {
    const awaiting = orders.filter((o) => o.status === "submitted");
    return {
        awaiting,
        drafts: orders.filter((o) => o.status === "draft").length,
        total: orders.length,
        valueIncGstCents: awaiting.reduce((sum, o) => sum + o.subtotalIncGstCents, 0),
    };
}
/**
 * GP across the menu, from a `analyseMenu` result.
 *
 * The weighted figure is the one the tile shows big, because it is the one
 * that answers "is the list making money". `meanGp` is kept beside it so the
 * dashboard and screen 07 can be reconciled by eye.
 */
export function menuGpSummary(analysis, target = analysis.gpTarget) {
    return {
        itemCount: analysis.items.length,
        meanGp: analysis.meanGp,
        weightedGp: analysis.weightedGp,
        totalWeeklyRevenue: cents(analysis.totalWeeklyRevenue),
        totalWeeklyMargin: cents(analysis.totalWeeklyMargin),
        target,
        belowTarget: analysis.items.filter((item) => item.gp < target).length,
        unpricedCount: analysis.unpricedSpecIds.length,
    };
}
/** The plan ceiling off `org.settings`, or `null` when none is on file. */
function ceilingFrom(settings) {
    const raw = settings
        ?.aiPlanCeilingCents;
    return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? raw : null;
}
/**
 * The org's AI budget out of `/v1/me`'s `org.settings`, and — when there is
 * none — which kind of nothing it is.
 *
 * Read structurally rather than off a type: the shared `Me` in the SPA does
 * not declare `settings` and is a file this screen may not edit, which is
 * the same reading the wastage and prep screens take.
 *
 * The tile asks for this rather than for `aiBudgetFrom`, because a tile that
 * disappears when the cap is missing reads as a bug: a brand-new
 * organisation has no cap, and the first thing its owner sees should be a
 * sentence saying so, not a gap where a figure was.
 *
 * This is the *cap*, not what is left of it; spend to date comes from
 * `GET /v1/org/usage`.
 */
export function aiBudgetStateFrom(settings) {
    const raw = settings
        ?.aiMonthlyLimitCents;
    const ceilingCents = ceilingFrom(settings);
    if (typeof raw !== "number" || !Number.isFinite(raw)) {
        return {
            kind: "none",
            absence: "unset",
            ceilingCents,
            title: "No monthly AI cap is set.",
            detail: ceilingCents === null
                ? "This organisation's settings name neither a cap nor a plan ceiling. Open screen 12 · Organisation to set a cap."
                : `No cap came back for this organisation. The plan allows up to ${money(ceilingCents / 100)} a month — set one on screen 12 · Organisation.`,
        };
    }
    if (raw <= 0) {
        return {
            kind: "none",
            absence: "zero",
            ceilingCents,
            title: "The monthly AI cap is $0.00, so every chat turn is refused.",
            detail: ceilingCents === null
                ? "Raise it on screen 12 · Organisation. No plan ceiling came back, so ask Venox if it will not hold."
                : `Raise it on screen 12 · Organisation — the plan allows up to ${money(ceilingCents / 100)} a month.`,
        };
    }
    return {
        kind: "set",
        budget: {
            limitCents: raw,
            ceilingCents,
            effectiveCents: ceilingCents === null ? raw : Math.min(raw, ceilingCents),
        },
    };
}
/**
 * The cap alone, or `null` when the org has not got one.
 *
 * Kept for callers that only want the figure; anything that has to *say*
 * something when there is no cap wants `aiBudgetStateFrom` instead, so it
 * can tell "never set" from "set to zero".
 */
export function aiBudgetFrom(settings) {
    const state = aiBudgetStateFrom(settings);
    return state.kind === "set" ? state.budget : null;
}
/** A count line's band, in the prototype's colours. */
export function bandTone(band) {
    return band === "ok" ? "pos" : band === "watch" ? "warn" : "neg";
}
/**
 * A GP figure against its target: on or over is good, within two points is
 * worth watching, further under is not.
 *
 * Two points is the prototype's own sense of the thing — its GP rows flag a
 * −0.4 pt move amber and leave +1.2 green.
 */
export const GP_WARN_POINTS = 2;
export function gpTone(gp, target = DEFAULT_GP_TARGET) {
    if (gp >= target)
        return "pos";
    return gp >= target - GP_WARN_POINTS ? "warn" : "neg";
}
/** Anything left to do is amber; a finished list is green. */
export function openTone(open) {
    return open === 0 ? "pos" : "warn";
}
/** Dollars for a tile: `$41.20`, `—` when there is nothing to show. */
export function tileMoney(dollars) {
    return typeof dollars === "number" && Number.isFinite(dollars) ? money(dollars) : "—";
}
//# sourceMappingURL=dashboard.js.map